// src/lib/docx-export.js: Genereert bewerkbare .docx voor de drie result-flows.
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
  Table, TableRow, TableCell, WidthType, BorderStyle, TableLayoutType,
} from "docx";
import { calc } from "./calc.js";
import { bulletsVoorRol, omschrijvingVoorRol } from "../data/rollen.js";
import { niveauIndicatorenFilmAv, sectieLabels } from "../data/niveau-indicatoren.js";

const FW_NAVY = "221659";
const FW_NAVY2 = "3F2F7A";
const FW_GRAY = "8580A8";

// ── Layout helpers ───────────────────────────────────────────────────────────

const p = (text, opts = {}) => new Paragraph({
  spacing: { after: 120, ...(opts.spacing || {}) },
  alignment: opts.alignment,
  children: [new TextRun({
    text,
    size: opts.size || 22, // 11pt
    bold: opts.bold,
    italics: opts.italics,
    color: opts.color || FW_NAVY2,
  })],
});

const h1 = (text) => new Paragraph({
  heading: HeadingLevel.HEADING_1,
  spacing: { before: 240, after: 160 },
  children: [new TextRun({ text, size: 36, bold: true, color: FW_NAVY })],
});

const h2 = (text) => new Paragraph({
  heading: HeadingLevel.HEADING_2,
  spacing: { before: 320, after: 120 },
  children: [new TextRun({ text, size: 28, bold: true, color: FW_NAVY })],
});

const h3 = (text) => new Paragraph({
  heading: HeadingLevel.HEADING_3,
  spacing: { before: 200, after: 80 },
  children: [new TextRun({ text, size: 24, bold: true, color: FW_NAVY })],
});

const bullet = (text) => new Paragraph({
  bullet: { level: 0 },
  spacing: { after: 80 },
  children: [new TextRun({ text, size: 22, color: FW_NAVY2 })],
});

// A4-pagina = 11906 twips, marges 1000 elk → bruikbare breedte ≈ 9906 twips.
// 35/65 split. Vaste kolombreedte in DXA + FIXED layout is nodig omdat Word
// PERCENTAGE-breedtes anders interpreteert als minimum, waardoor cellen
// per teken wrappen (verticale tekst).
const KV_COL_LEFT = 3467;
const KV_COL_RIGHT = 6439;

const kvTable = (rows) => new Table({
  width: { size: KV_COL_LEFT + KV_COL_RIGHT, type: WidthType.DXA },
  columnWidths: [KV_COL_LEFT, KV_COL_RIGHT],
  layout: TableLayoutType.FIXED,
  borders: noBorders(),
  rows: rows.map(([k, v]) => new TableRow({
    children: [
      new TableCell({
        width: { size: KV_COL_LEFT, type: WidthType.DXA },
        children: [new Paragraph({
          spacing: { after: 60 },
          children: [new TextRun({ text: k, bold: true, size: 22, color: FW_NAVY })],
        })],
      }),
      new TableCell({
        width: { size: KV_COL_RIGHT, type: WidthType.DXA },
        children: [new Paragraph({
          spacing: { after: 60 },
          children: [new TextRun({ text: String(v), size: 22, color: FW_NAVY2 })],
        })],
      }),
    ],
  })),
});

const noBorders = () => {
  const none = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  return { top: none, bottom: none, left: none, right: none, insideHorizontal: none, insideVertical: none };
};

// Header met logo-tekst + datum, één blok bovenaan elk document.
const documentHeader = () => {
  const datum = new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  return [
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 60 },
      children: [new TextRun({ text: "Filmwerkwijs · Fair Pay calculatie 2026", size: 18, color: FW_GRAY, bold: true })],
    }),
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 200 },
      children: [new TextRun({ text: datum, size: 18, color: FW_GRAY })],
    }),
  ];
};

// Disclaimer-tekst als gedeelde bron, zodat de Word-export en de kopieer-
// teksten (e-mail / gespreksvoorbereiding) niet uit elkaar lopen.
export const disclaimerTekst = (sector = "film/AV-sector") =>
  `Gegenereerd via filmwerkwijs.nl: Fair Pay richtlijnen 2026 voor de Nederlandse ${sector}. Deze informatie is gebaseerd op de Fair Pay richtlijnen zoals vastgelegd door de Ketentafels Film & AV-productie en Filmfestivals en gepubliceerd op https://fairpacct.nl bij de betreffende ketentafel. Filmwerkwijs verstrekt algemene informatie en is geen juridisch of financieel adviseur. Aan de verstrekte informatie kunnen geen rechten worden ontleend. Individuele situaties kunnen afwijken; raadpleeg bij twijfel een juridisch of financieel adviseur.`;

const voetnoot = (sector = "film/AV-sector") => [
  new Paragraph({
    spacing: { before: 320, after: 60 },
    children: [new TextRun({ text: disclaimerTekst(sector), size: 18, italics: true, color: FW_GRAY })],
  }),
];

const detailRegelsVoorCategorie = (urenDetails, cat) => {
  const rijen = Array.isArray(urenDetails?.[cat]) ? urenDetails[cat].filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
  return rijen.map(r => bullet(`${r.activiteit}: ${r.uren} uur`));
};

// Meerregelige tekst (Opdracht Kompas / rol-omschrijving) als losse paragrafen
// renderen. Lege regels worden alinea-scheidingen, enkele \n's gewone breaks.
const multiLineP = (tekst, opts = {}) => {
  if (!tekst) return [];
  return String(tekst).split(/\n{2,}/).map(blok => p(blok.replace(/\n/g, " ").trim(), opts));
};

// "Rol / functie"-blok: bronzin + bullets per rol uit de Gids (Film/AV) of
// de omschrijving uit het Functiehandboek Filmfestivals (festival).
const rolFunctieSectie = (huidigeRol, isFestival) => {
  const bron = isFestival
    ? "Op basis van jouw keuze uit het Functiehandboek Referentiefuncties Filmfestivals."
    : "Op basis van jouw keuze in de rollen-lijst is deze rol gekozen uit de Gids Rollen Film/AV.";
  const blokken = [
    h2(`Rol: ${huidigeRol.rol} · ${huidigeRol.dep}`),
    p(bron, { italics: true, color: FW_GRAY }),
  ];
  if (isFestival) {
    const omschrijving = omschrijvingVoorRol("festival", huidigeRol.dep, huidigeRol.rol);
    if (omschrijving) blokken.push(...multiLineP(omschrijving));
    blokken.push(p("Zie voor het uitgebreide profiel van de Referentiefunctie het Functiehandboek Referentiefuncties Filmfestivals.", { italics: true, color: FW_GRAY }));
  } else {
    const bullets = bulletsVoorRol(huidigeRol.dep, huidigeRol.rol);
    if (bullets?.length) blokken.push(...bullets.map(b => bullet(b)));
  }
  return blokken;
};

// Zwaarteniveau-blok voor Film/AV: kaderzin + niveau-indicatoren. De rol-
// specifieke beschrijving zit al in de Rol-sectie (bullets uit de Gids).
const zwaarteNiveauSectieFilmAv = (huidigeRol, uurtarief) => {
  const blokken = [
    h2(`Zwaarteniveau ${huidigeRol.niveau}`),
    p(`Het Opdracht Kompas koppelt elke opdracht aan een zwaarteniveau. Niveau ${huidigeRol.niveau} geeft het minimum starttarief van ${calc.fmt(uurtarief)}/u.`),
  ];

  const indicatoren = niveauIndicatorenFilmAv[huidigeRol.niveau];
  if (indicatoren) {
    blokken.push(h3(`Niveau-indicatoren: niveau ${huidigeRol.niveau} · USB-punten ${indicatoren.usb}`));
    Object.entries(indicatoren.secties).forEach(([key, tekst]) => {
      blokken.push(p(sectieLabels[key] || key, { bold: true, spacing: { before: 80, after: 40 } }));
      blokken.push(p(tekst));
    });
  }

  return blokken;
};

// ── Film & AV (zzp) ──────────────────────────────────────────────────────────

export function exportFilmAv({ state, opdracht, huidigeRol, rolResultaat }) {
  const urenDetails = huidigeRol.urenDetails || {};
  const cats = [
    { key: "voorbereiding", label: "Voorbereiding", uren: rolResultaat.urenVoorbereiding },
    { key: "hoofdwerk", label: "Hoofdwerkzaamheden", uren: rolResultaat.urenHoofdwerk },
    { key: "afronding", label: "Afronding", uren: rolResultaat.urenAfronding },
    { key: "pr_promotie", label: "PR & promotie", uren: rolResultaat.urenPrPromotie },
  ].filter(c => c.uren > 0);

  const children = [
    ...documentHeader(),
    h1("Openingsbod film/AV-productie"),
    p(`Voor mijn inzet als ${huidigeRol.rol} (${huidigeRol.dep}).`),

    h2("Samenvatting"),
    kvTable([
      ["Type opdracht", "Film/AV-productie (zzp)"],
      ["Rol · departement", `${huidigeRol.rol} · ${huidigeRol.dep}`],
      ["Zwaarteniveau", `${huidigeRol.niveau}${rolResultaat.tariefRij?.domein ? ` (${rolResultaat.tariefRij.domein})` : ""}`],
      ["Uurtarief", `${calc.fmt(rolResultaat.uurtarief)} (incl. 74,45% opslag)`],
      ["Voorbeeld dagtarief (8,5 u)", calc.fmt(rolResultaat.uurtarief * 8.5)],
      ["Voorbeeld dagtarief (9,5 u)", calc.fmt(rolResultaat.uurtarief * 9.5)],
      ["Totaal uren", `${rolResultaat.urenTotaal} uur`],
      ["Openingsbod (excl. btw)", calc.fmtInt(rolResultaat.openingsbod)],
      ["Totaal incl. 21% btw", calc.fmtInt(opdracht.totaalInclBtw)],
    ]),

    ...rolFunctieSectie(huidigeRol, false),
    ...zwaarteNiveauSectieFilmAv(huidigeRol, rolResultaat.uurtarief),

    ...(state.omschrijving ? [
      h2("Omschrijving van de opdracht"),
      p(state.omschrijving),
    ] : []),

    h2("Tijdsinvestering per categorie"),
    ...cats.flatMap(c => [
      h3(`${c.label}: ${c.uren} uur`),
      ...detailRegelsVoorCategorie(urenDetails, c.key),
    ]),

    h2("Openingsaanbod"),
    ...cats.map(c => p(`${c.label}: ${c.uren} uur × ${calc.fmt(rolResultaat.uurtarief)} = ${calc.fmt(c.uren * rolResultaat.uurtarief)}`)),
    p(`Subtotaal opdracht (${rolResultaat.urenTotaal} uur): ${calc.fmt(opdracht.subtotaalArbeid)}`, { bold: true }),
    p(`21% btw: ${calc.fmt(opdracht.btw)}`),
    p(`Totaal incl. btw: ${calc.fmt(opdracht.totaalInclBtw)}`, { bold: true }),

    h2("Aandachtspunten voor de onderhandeling"),
    p("Dit openingsbod is het minimum starttarief volgens Fair Pay richtlijnen 2026. Bij de onderhandeling kun je denken aan:"),
    bullet("Jouw relevante ervaring, reputatie of specialisme;"),
    bullet("Bijzondere werktijden en -locaties;"),
    bullet("De looptijd van een opdracht;"),
    bullet("Eventueel declarabel over- of meerwerk;"),
    bullet("Je opdrachtgever kan een andere inschatting van de werkzaamheden, de tijd, het niveau en daarmee het starttarief of totale tarief hebben gemaakt;"),
    bullet("Gegarandeerde beschikbaarheid voor inzet binnen de productie gedurende een langere periode."),

    h2("Goed om te benoemen"),
    bullet("Dit is een minimum starttarief volgens Fair Pay richtlijnen 2026. Ervaring of specifieke expertise zijn onderhandelingsruimte."),
    bullet("Voor reiskosten, apparatuur, materialen of inhuur derden kun je separaat afspraken maken."),
    bullet("Het tarief bevat al een opslag van 74,45% voor o.a. zzp-lasten, niet-declarabele-tijd en niet-declarabele kosten (vakantie, pensioen, ZVW)."),

    ...voetnoot(),
  ];

  return buildDoc(children, `Fair-Pay-openingsbod-${slug(huidigeRol.rol)}.docx`);
}

// ── Festival zzp ─────────────────────────────────────────────────────────────

export function exportFestivalZzp({ state, huidigeRol, result, totaalExBtw, btw, totaalInclBtw }) {
  const fdRegel = result.festivaldagen > 0 ? `, waarvan ${result.festivaldagen} festivaldag${result.festivaldagen === 1 ? "" : "en"}` : "";

  const children = [
    ...documentHeader(),
    h1("Openingsbod filmfestival (zzp)"),
    p(`Voor mijn inzet als ${huidigeRol.rol} (${huidigeRol.dep}) op jouw filmfestival. Onderbouwing op basis van de Fair Pay richtlijnen 2026 voor filmfestivals (schaal ${huidigeRol.niveau} volgens CAO Toneel & Dans).`),

    h2("Samenvatting"),
    kvTable([
      ["Type opdracht", "Filmfestival: zzp"],
      ["Rol · departement", `${huidigeRol.rol} · ${huidigeRol.dep}`],
      ["Schaal", `${huidigeRol.niveau}${result.tariefRij?.rol_voorbeeld ? ` (${result.tariefRij.rol_voorbeeld})` : ""}`],
      ["Uurtarief", `${calc.fmt(result.uurtarief)} (incl. 69,26% opslag)`],
      ["Dagtarief", `${calc.fmt(result.dagtarief)} (× 7,6 u)`],
      ["Werkdagen", `${result.werkdagenTotaal} dagen${fdRegel}`],
      ["Openingsbod (excl. btw)", calc.fmtInt(result.openingsbod)],
      ["Totaal incl. 21% btw", calc.fmtInt(totaalInclBtw)],
    ]),

    ...rolFunctieSectie(huidigeRol, true),

    ...(state.omschrijving ? [
      h2("Omschrijving van de opdracht"),
      p(state.omschrijving),
    ] : []),

    h2("Onderbouwing"),
    p(`${result.werkdagenTotaal} werkdagen × ${calc.fmt(result.dagtarief)} = ${calc.fmt(result.openingsbod)}`),
    p(`Totaal excl. btw: ${calc.fmt(totaalExBtw)}`, { bold: true }),
    p(`21% btw: ${calc.fmt(btw)}`),
    p(`Totaal incl. btw: ${calc.fmt(totaalInclBtw)}`, { bold: true }),

    h2("Aandachtspunten voor de onderhandeling"),
    p("Dit dagtarief is het minimum volgens Fair Pay. De Standaard Basisregeling SAV Filmfestivals geeft je extra grond voor:"),
    bullet("Festivaldag-toeslag: een opslag op dagen tijdens het festival zelf."),
    bullet("Nachtwerk: 25% toeslag op uren tussen 22:00 en 06:00."),
    bullet("Reisuren: gelden als arbeidstijd en worden volledig vergoed."),
    bullet("Lunch en diner: tijdens het festival door het festival aangeboden."),
    bullet("Reis- en verblijfkosten, materiaal en inhuur kun je apart factureren bovenop het dagtarief."),

    h2("Goed om te benoemen"),
    bullet("Dit is een minimum dagtarief volgens Fair Pay. Meer ervaring, complexiteit en SAV-toeslagen zijn onderhandelruimte."),
    bullet("Reis-, materiaal- en inhuurkosten mag je apart factureren bovenop het dagtarief."),
    bullet("Het tarief bevat al een opslag van 69,26% voor zzp-lasten, niet-declarabele tijd, vakantie, ziekte en pensioen."),

    ...voetnoot("filmfestivals"),
  ];

  return buildDoc(children, `Fair-Pay-openingsbod-festival-${slug(huidigeRol.rol)}.docx`);
}

// ── Festival loondienst ──────────────────────────────────────────────────────

export function exportFestivalLoondienst({ state, huidigeRol, result }) {
  const periodeRegels = result.perBlok.map(b =>
    p(`${b.maanden} maanden × ${b.pct}% van fulltime = ${calc.fmt(b.brutoMaand)} bruto/maand`)
  );
  const gemiddeldBrutoMaand = result.totaalMaanden > 0 ? result.totaalBruto / result.totaalMaanden : 0;

  const children = [
    ...documentHeader(),
    h1("Salarisindicatie filmfestival (loondienst)"),
    p(`Voor mijn inzet als ${huidigeRol.rol} (${huidigeRol.dep}).`),

    h2("Samenvatting"),
    kvTable([
      ["Type opdracht", "Filmfestival: loondienst"],
      ["Rol · departement", `${huidigeRol.rol} · ${huidigeRol.dep}`],
      ["Schaal · periodiek", `Schaal ${huidigeRol.niveau} · periodiek ${result.periodiek}`],
      ["Bruto fulltime / maand", calc.fmt(result.brutoFulltime)],
      ["Periode", `${result.totaalMaanden} maanden`],
      ["Gemiddeld bruto / maand", calc.fmtInt(gemiddeldBrutoMaand)],
      ["Totaal periode", calc.fmtInt(result.totaalBruto)],
    ]),

    ...rolFunctieSectie(huidigeRol, true),

    h2("Onderbouwing per dienstverband-blok"),
    ...periodeRegels,
    p(`Totaal periode (${result.totaalMaanden} mnd): ${calc.fmt(result.totaalBruto)}`, { bold: true }),
    p(`Gemiddeld /maand (gewogen): ${calc.fmt(gemiddeldBrutoMaand)}`),

    ...(state.omschrijving ? [
      h2("Omschrijving van de opdracht"),
      p(state.omschrijving),
    ] : []),

    h2("Aandachtspunten voor het gesprek"),
    p("Dit bruto-bedrag is de ondergrens. In een loondienst-gesprek gaat het verder over de secundaire voorwaarden:"),
    bullet("Vakantiegeld (8%) en eindejaarsuitkering: meestal bovenop het bruto."),
    bullet("Pensioenbijdrage werkgever: vraag naar de regeling en de werkgeversbijdrage."),
    bullet("Compensatie in tijd voor festivaldagen, weekend- en avondwerk."),
    bullet("Reiskosten en eventueel thuiswerkvergoeding."),
    bullet("Vertrouwenspersoon en sociale veiligheid: moet bij het festival belegd zijn."),

    h2("Goed om te benoemen"),
    bullet("Dit is de ondergrens op basis van de Salaristabel Filmfestivals en het Functiehandboek Filmfestivals. De werkgever mag hoger inschalen op basis van ervaring of complexiteit."),
    bullet("Vakantiegeld, pensioen, vakantiedagen en eventuele eindejaarsuitkering komen bovenop dit bruto bedrag."),
    bullet("Bij variabele inzet per maand kun je vragen om compensatie in tijd of een vaste opslag voor onregelmatige uren."),

    ...voetnoot("filmfestivals"),
  ];

  return buildDoc(children, `Fair-Pay-salarisindicatie-${slug(huidigeRol.rol)}.docx`);
}

// ── Build + download helpers ─────────────────────────────────────────────────

function buildDoc(children, filename) {
  const doc = new Document({
    creator: "Filmwerkwijs",
    title: filename.replace(/\.docx$/, ""),
    description: "Fair Pay calculatie 2026",
    styles: {
      default: {
        document: { run: { font: "Calibri", color: FW_NAVY2 } },
      },
    },
    sections: [{
      properties: { page: { margin: { top: 1000, right: 1000, bottom: 1000, left: 1000 } } },
      children,
    }],
  });
  return Packer.toBlob(doc).then(blob => {
    downloadBlob(blob, filename);
    return filename;
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "rol";
}
